import {AbstractMatch, Autolinker, AutolinkerConfig, Match, ReplaceFnReturn, UrlMatch} from 'autolinker';
import {Injectable} from '@angular/core';
import {AceLoggerService} from '@logging/ace-logger.service';
import {AceLogger} from '@logging/ace-logger';
import {HtmlUtils} from '@shared/util/general/html-utils';
import {MessageTranslateService} from '@shared/services/i18n/translate-service-service.service';
import {IconName} from '@fortawesome/fontawesome-svg-core';
import {TicketUtils} from '@app/layout/sites/tickets/ticket-utils';
import {User} from '@app/layout/admin/services/api/user-interfaces';
import {parseMentionMatches, MentionMatch} from './mention-match';
import {parseEmailMatches} from './email-match';

interface SpanOptions {
  class?: string;
  content?: string;
  title?: string;
}

export interface CustomLinkifyConfig {
  usersByEmail?: Map<string, User>;
  previewMode?: boolean;
  autolinkerConfig?: AutolinkerConfig;
}

@Injectable({
  providedIn: 'root'
})
export class LinkifyService {
  readonly LINKY_CSS_CLASS_NAME = 'detected-link';

  readonly LINKY_OPTIONS: AutolinkerConfig = {
    stripPrefix: false,
    stripTrailingSlash: false,
    truncate: {length: 80},
    className: this.LINKY_CSS_CLASS_NAME,
    phone: false,
    email: false,
    sanitizeHtml: true,
    replaceFn: this.linkReplacementFunction.bind(this)
  };

  private readonly platformHostnames: Set<string> = new Set([
      'ace.cimenviro.com',
      'ace-test.cimenviro.com',
      'ace.cim.io',
      'ace-test.cim.io',
      'peak.cimenviro.com',
      'peak-test.cimenviro.com',
      'peak.cim.io',
      'peak-test.cim.io'
  ]);
  private readonly logger: AceLogger;
  private autolinkerByConfig: Map<AutolinkerConfig, Autolinker>;
  private customConfig?: CustomLinkifyConfig;

  constructor(private translateService: MessageTranslateService, logService: AceLoggerService) {
    this.logger = logService.create(LinkifyService.name);
    this.autolinkerByConfig = new Map([[this.LINKY_OPTIONS, new Autolinker(this.LINKY_OPTIONS)]]);
  }

  public linky(src: string, customConfig?: CustomLinkifyConfig): string {
    this.customConfig = customConfig;
    return this.customLink(src, customConfig?.autolinkerConfig);
  }

  public isPlatformURL(url: URL): boolean {
    const currentOrigin = window.location.origin;
    return !!(url && (currentOrigin === url.origin || (url.host && this.platformHostnames.has(url.host))));
  }

  /** In preview mode, hide internal links and prevent all links from being clickable */
  private linkReplacementFunction(match: Match): ReplaceFnReturn {
    const isPreviewMode = this.customConfig?.previewMode ?? false;
    switch(<string>match.type) {
      case 'url' :
        const urlMatch = <UrlMatch> match;
        try {
          const url = new URL(urlMatch.getUrl());
          if (this.isPlatformURL(url)) {
            const urlSegments = HtmlUtils.getPathSegments(url);
            const isAttachmentUrl = TicketUtils.isAttachmentUrl(urlSegments);
            const identifierSegment = urlSegments[0];
            const isChartUrl = identifierSegment === 'charts';
            const isMeterUrl = identifierSegment === 'meters';
            const tag = urlMatch.buildTag();
            if (isPreviewMode) {
              if (isAttachmentUrl || isChartUrl || isMeterUrl) {
                return '';
              } else {
                return false;
              }
            } else {
              if (isChartUrl) {
                tag.setInnerHTML(this.createChartTypeLinkText('View Chart'));
              } else if (isMeterUrl) {
                tag.setInnerHTML(this.createChartTypeLinkText('View Meter Chart'));
              } else if (isAttachmentUrl) {
                const fileName = urlSegments[6] ? decodeURIComponent(urlSegments[6]) : null;
                const message = fileName ?? this.translateService.instant({messageKey: 'View Attachment'});
                tag.setInnerHTML(this.createIconHtmlString('paperclip') + HtmlUtils.escapeHTML(message));
              }
            }
            const attributes = tag.getAttrs() ?? {};
            const relValues = attributes['rel']?.split(' ')?.filter(value => value !== 'noopener' && value !== 'noreferrer') ?? [];
            relValues.push('opener');
            if (relValues?.length) {
              tag.setAttr('rel', relValues.join(' '));
            } else {
              delete attributes['rel'];
              tag.setAttrs(attributes);
            }

            return tag;
          } else if (isPreviewMode){
            return false;
          }
        } catch (e) {
          this.logger.error('Error while parsing a link', e);
        }

        return true;  // let Autolinker perform its normal anchor tag replacement
      case MentionMatch.matchName :
        const matchedText = match.getMatchedText();
        const mentionedEmail = matchedText.slice(TicketUtils.MENTION_CHARACTER.length);
        const mentionedUser = this.customConfig?.usersByEmail?.get(mentionedEmail);
        if (mentionedUser) {
          const title = mentionedUser.email + ' [' + mentionedUser.entity.name + ']';
          return this.wrapWithSpan({class: isPreviewMode ? null : 'fw-bold', content: '@' + mentionedUser.fullName, title});
        } else if (isPreviewMode) {
          return false;
        } else {
          const tag = match.buildTag();
          tag.setInnerHTML(mentionedEmail);
          return matchedText.slice(0, TicketUtils.MENTION_CHARACTER.length) + tag.toAnchorString();
        }
      default:
        return !isPreviewMode; // let Autolinker perform its normal anchor tag replacement when not in preview mode
    }
  }

  // using span instead of SVG so that it is not stripped away by Angular sanitisation 
  private createIconHtmlString(faIconClass: IconName, otherClasses?: string, title?: string): string {
    return this.wrapWithSpan({ class: otherClasses + ' fas fa-' + faIconClass, title });
  }

  private createChartTypeLinkText(messageKey: string): string {
    return this.createIconHtmlString('chart-line') + HtmlUtils.escapeHTML(this.translateService.instant({messageKey}));
  }

  private getAutolinker(options: AutolinkerConfig = this.LINKY_OPTIONS): Autolinker {
    const autolinkerInstance = this.autolinkerByConfig.get(options);
    if (autolinkerInstance) {
      return autolinkerInstance;
    } else {
      const newInstance = new Autolinker(options);
      this.autolinkerByConfig.set(options, newInstance);
      return newInstance;
    }
  }

  /** This is a copy of `Autolinker.link` but customized to add mentionMatch[] & emailMatch[] that replaceFn can replace.
   * `(<any>autolinker).*` is for calling private methods */
  private customLink(src: string, options?: AutolinkerConfig): string {
    if (!src) {
      return '';
    }
    if ((options || this.LINKY_OPTIONS)?.sanitizeHtml) {
      src = src.replace(/</g, '&lt;').replace(/>/g, '&gt;');
    }
    // customised part to get autolinker & tagbuilder instance
    const autolinker = this.getAutolinker(options),
      tagBuilder = (<any>autolinker).getTagBuilder();

    let newHtml: string[] = [],
      matches: AbstractMatch[] = autolinker.parse(src),
      lastIndex = 0;

    // customised part to add custom matches. Must sort so `match.offset` are correct when reconstructing the newHtml
    matches.push(...parseMentionMatches(src, tagBuilder, matches));
    matches.push(...parseEmailMatches(src, tagBuilder, matches));
    matches.sort((a,b) => a.getOffset() - b.getOffset());

    for (let i = 0, len = matches.length; i < len; i++) {
      let match = matches[i];
      newHtml.push(src.substring(lastIndex, match.getOffset()));
      newHtml.push((<any>autolinker).createMatchReturnVal(match));
      lastIndex = match.getOffset() + match.getMatchedText().length;
    };
    newHtml.push(src.substring(lastIndex));

    return newHtml.join('');
  }

  private wrapWithSpan(options: SpanOptions): string {
    const classAttr = options.class ? ' class="' + HtmlUtils.escapeHTML(options.class) + '"' : '';
    const titleAttr = options.title ? ' title="' + HtmlUtils.escapeHTML(options.title) + '"' : '';
    return '<span' + classAttr + titleAttr + '>' + (HtmlUtils.escapeHTML(options.content) ?? '') + '</span>';
  }
}